import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { CONTAINER_COLORS } from '../utils/container-colors';
import type { ImportOrderItem } from '../types/import-tracking.types';
import { TariffPicker } from '@/features/trade/components/tariff-picker';
import type { TariffCodeInfo } from '@/features/trade/types/trade.types';

// NaN (de valueAsNumber con input vacío) → undefined
const nanToUndefined = z.preprocess(
  (val) => (typeof val === 'number' && Number.isNaN(val) ? undefined : val),
  z.number().optional()
);

const itemFormSchema = z.object({
  description: z.string().min(1, 'Descripción requerida'),
  hsCode: z.string().optional(),
  quantity: z.number().positive('Cantidad debe ser mayor a 0'),
  unitOfMeasure: z.string().min(1, 'Unidad de medida requerida'),
  unitFobPrice: z.number().nonnegative('Precio FOB debe ser >= 0'),
  weightKg: nanToUndefined,
  // Dimensiones por unidad — el volumeCbm se calcula auto en el backend
  // si están y no se pasa volumeCbm explícito.
  lengthCm: nanToUndefined,
  widthCm: nanToUndefined,
  heightCm: nanToUndefined,
  colorHex: z.string().optional(),
  dutyRate: z.preprocess(
    (val) => (typeof val === 'number' && Number.isNaN(val) ? undefined : val),
    z.number().min(0, 'Mínimo 0%').max(100, 'Máximo 100%').optional()
  ),
});

type ItemFormData = z.infer<typeof itemFormSchema>;

const UNIT_OPTIONS = [
  { value: 'UND', label: 'UND - Unidades' },
  { value: 'KG', label: 'KG - Kilogramos' },
  { value: 'LT', label: 'LT - Litros' },
  { value: 'MT', label: 'MT - Metros' },
  { value: 'M2', label: 'M2 - Metros cuadrados' },
  { value: 'M3', label: 'M3 - Metros cúbicos' },
  { value: 'TON', label: 'TON - Toneladas' },
  { value: 'PAR', label: 'PAR - Pares' },
  { value: 'CAJA', label: 'CAJA - Cajas' },
  { value: 'ROLLO', label: 'ROLLO - Rollos' },
];

interface ProductFormDialogProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: ImportOrderItem;
  onSuccess?: () => void;
}

export function ProductFormDialog({
  orderId,
  open,
  onOpenChange,
  item,
  onSuccess,
}: ProductFormDialogProps) {
  const { addOrderItem, updateOrderItem } = useImportTrackingStore();
  const isEdit = !!item;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ItemFormData>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      description: '',
      hsCode: '',
      quantity: undefined as unknown as number,
      unitOfMeasure: 'UND',
      unitFobPrice: undefined as unknown as number,
      weightKg: undefined,
      lengthCm: undefined,
      widthCm: undefined,
      heightCm: undefined,
      colorHex: CONTAINER_COLORS[0],
      dutyRate: undefined,
    },
  });

  /**
   * La partida elegida del catálogo oficial.
   *
   * Vive fuera del formulario porque lo que se guarda es su **id**, y el objeto
   * completo hace falta en pantalla para mostrar el código, la descripción y las
   * alícuotas mientras se carga el producto.
   */
  const [partida, setPartida] = useState<TariffCodeInfo | null>(null);

  const selectedUnit = watch('unitOfMeasure');
  const watchQuantity = watch('quantity');
  const watchUnitFob = watch('unitFobPrice');
  const watchL = watch('lengthCm');
  const watchW = watch('widthCm');
  const watchH = watch('heightCm');
  const selectedColor = watch('colorHex');

  // Volumen calculado en vivo desde dimensiones
  const previewVolumeCbm = useMemo(() => {
    if (!watchL || !watchW || !watchH) return null;
    return (watchL * watchW * watchH) / 1_000_000;
  }, [watchL, watchW, watchH]);

  useEffect(() => {
    if (!open) return;
    if (item) {
      reset({
        description: item.description,
        hsCode: item.hsCode || '',
        quantity: item.quantity,
        unitOfMeasure: item.unitOfMeasure,
        unitFobPrice: item.unitFobPrice,
        weightKg: item.weightKg ?? undefined,
        lengthCm: item.lengthCm ?? undefined,
        widthCm: item.widthCm ?? undefined,
        heightCm: item.heightCm ?? undefined,
        colorHex: item.colorHex ?? CONTAINER_COLORS[0],
        dutyRate: item.dutyRate != null ? item.dutyRate * 100 : undefined,
      });
    } else {
      reset({
        description: '',
        hsCode: '',
        quantity: undefined as unknown as number,
        unitOfMeasure: 'UND',
        unitFobPrice: undefined as unknown as number,
        weightKg: undefined,
        lengthCm: undefined,
        widthCm: undefined,
        heightCm: undefined,
        colorHex: CONTAINER_COLORS[Math.floor(Math.random() * CONTAINER_COLORS.length)],
        dutyRate: undefined,
      });
    }
  }, [open, item, reset]);

  const onSubmit = async (data: ItemFormData) => {
    try {
      const payload = {
        ...data,
        hsCode: partida?.code ?? data.hsCode ?? undefined,
        /*
          El id de la partida es lo que de verdad importa.

          `dutyRate` y `hsCode` quedan como respaldo para las órdenes viejas y
          para que la fila se pueda leer sin consultar el catálogo, pero el
          cálculo resuelve la alícuota por el id: así un cambio de decreto se
          propaga a todas las órdenes sin tocar ninguna.
        */
        tariffHeadingId: partida?.id ?? null,
        // La alícuota del catálogo viene en puntos (10 = 10 %); el backend la
        // guarda igual y la convierte a fracción al liquidar.
        dutyRate: partida ? partida.gaRate : undefined,
      };
      if (isEdit && item) {
        await updateOrderItem(orderId, item.id, payload);
      } else {
        await addOrderItem(orderId, payload);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Error manejado por el store
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Item' : 'Agregar Item'}</DialogTitle>
          <DialogDescription>
            Cargá los datos del item. Las dimensiones (LxWxH) calculan automáticamente el volumen.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Descripción */}
          <div>
            <Label htmlFor="description">Descripción *</Label>
            <Input
              id="description"
              {...register('description')}
              placeholder="Ej: Caja con notebooks Lenovo..."
            />
            {errors.description && (
              <p className="text-xs text-destructive mt-1">{errors.description.message}</p>
            )}
          </div>

          {/*
            La partida arancelaria, elegida del catálogo oficial.

            Antes acá había un campo de texto libre para el código y otro para
            escribir la alícuota a mano. Eso hacía que el arancel de una
            importación saliera de lo que alguien recordaba, y que las 8.109
            partidas del Asistente Arancelario no llegaran nunca al cálculo.

            Al elegir se guarda el **id** de la partida, no solo su código: eso
            es lo que permite que la alícuota se resuelva contra el catálogo en
            cada recálculo, y que un cambio de decreto se propague solo.
          */}
          <div>
            <Label>Partida arancelaria</Label>
            <TariffPicker
              value={partida}
              onChange={(p) => {
                setPartida(p);
                setValue('hsCode', p?.code ?? '');
                // La alícuota deja de escribirse: sale de la partida.
                setValue('dutyRate', p ? p.gaRate : undefined);
              }}
            />
            {!partida && (
              <p className="mt-1 text-xs text-muted-foreground">
                Sin partida, este ítem no liquida arancel y el costo nacionalizado queda incompleto.
              </p>
            )}
          </div>

          {/* Cantidad + Unidad */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="number"
                step="any"
                {...register('quantity', { valueAsNumber: true })}
                placeholder="0"
              />
              {errors.quantity && (
                <p className="text-xs text-destructive mt-1">{errors.quantity.message}</p>
              )}
            </div>
            <div className="col-span-5">
              <Label>Unidad *</Label>
              <SearchableSelect
                options={UNIT_OPTIONS}
                value={selectedUnit || ''}
                onValueChange={(val) => setValue('unitOfMeasure', val)}
                placeholder="Seleccionar..."
                searchPlaceholder="Buscar unidad..."
              />
              {errors.unitOfMeasure && (
                <p className="text-xs text-destructive mt-1">{errors.unitOfMeasure.message}</p>
              )}
            </div>
          </div>

          {/* Dimensiones por unidad */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Dimensiones por unidad
            </Label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <div>
                <Label htmlFor="lengthCm" className="text-xs font-normal">
                  Largo (cm)
                </Label>
                <Input
                  id="lengthCm"
                  type="number"
                  step="0.1"
                  min="0"
                  {...register('lengthCm', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="widthCm" className="text-xs font-normal">
                  Ancho (cm)
                </Label>
                <Input
                  id="widthCm"
                  type="number"
                  step="0.1"
                  min="0"
                  {...register('widthCm', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="heightCm" className="text-xs font-normal">
                  Alto (cm)
                </Label>
                <Input
                  id="heightCm"
                  type="number"
                  step="0.1"
                  min="0"
                  {...register('heightCm', { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
            </div>
            {previewVolumeCbm != null && previewVolumeCbm > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Volumen calculado:{' '}
                <span className="font-mono font-semibold text-foreground">
                  {previewVolumeCbm.toFixed(4)} m³
                </span>{' '}
                por unidad
              </p>
            )}
          </div>

          {/* Peso + Precio FOB */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="weightKg">Peso por unidad (kg)</Label>
              <Input
                id="weightKg"
                type="number"
                step="any"
                min="0"
                {...register('weightKg', { valueAsNumber: true })}
                placeholder="0.0"
              />
            </div>
            <div>
              <Label htmlFor="unitFobPrice">Precio FOB unitario (USD) *</Label>
              <Input
                id="unitFobPrice"
                type="number"
                step="any"
                min="0"
                {...register('unitFobPrice', { valueAsNumber: true })}
                placeholder="0.00"
              />
              {errors.unitFobPrice && (
                <p className="text-xs text-destructive mt-1">{errors.unitFobPrice.message}</p>
              )}
            </div>
          </div>

          {/* Color */}
          <div>
            <Label>Color (visualización en plano)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CONTAINER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    selectedColor === color
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:border-muted-foreground/50'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setValue('colorHex', color)}
                  aria-label={`Color ${color}`}
                />
              ))}
            </div>
          </div>

          {/*
            La alícuota, de solo lectura.

            Acá había un campo para escribirla a mano, con un aviso en ámbar que
            pedía "Ingresá la tasa arancelaria para este código HS". Ese pedido
            era el problema: la tasa no la sabe quien carga el producto, la sabe
            el arancel. Ahora sale de la partida elegida y se muestra para que se
            pueda verificar, no para que se invente.
          */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <Label className="text-xs text-muted-foreground">Gravamen arancelario</Label>
            {partida ? (
              <p className="mt-0.5 text-sm">
                <span className="font-mono text-lg font-semibold tabular-nums">
                  {partida.gaRate} %
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  de la partida {partida.code} — arancel oficial
                </span>
              </p>
            ) : (
              <p className="mt-0.5 text-sm text-muted-foreground">
                Elegí una partida arriba y la alícuota sale del arancel.
              </p>
            )}
          </div>

          {/* Vista previa */}
          {watchQuantity > 0 && watchUnitFob > 0 && (
            <div className="rounded-lg bg-muted/50 border p-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Vista previa</p>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  FOB total ({watchQuantity} × USD {watchUnitFob})
                </span>
                <span className="font-medium tabular-nums">
                  USD{' '}
                  {(watchQuantity * watchUnitFob).toLocaleString('es-BO', {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
              {previewVolumeCbm != null && previewVolumeCbm > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Volumen total</span>
                  <span className="font-medium tabular-nums">
                    {(previewVolumeCbm * watchQuantity).toFixed(4)} m³
                  </span>
                </div>
              )}
              {/*
                ⚠ Esto es una referencia sobre el FOB, NO el arancel que se va a
                liquidar. El de verdad sale del CIF —FOB más la parte del flete y
                del seguro que le toca a este ítem— y eso no se puede saber hasta
                que estén cargados todos los productos y los tramos.

                Se rotula como "sobre el FOB" a propósito: la cifra real siempre
                va a ser MAYOR, y alguien que vea "arancel estimado" a secas y
                después el de Costos va a pensar que uno de los dos está mal.
              */}
              {partida != null && partida.gaRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Arancel {partida.gaRate} % sobre el FOB
                    <span className="ml-1 text-[10px]">(el real sale del CIF, en Costos)</span>
                  </span>
                  <span className="font-medium tabular-nums text-alerta">
                    ~USD{' '}
                    {(watchQuantity * watchUnitFob * (partida.gaRate / 100)).toLocaleString(
                      'es-BO',
                      { minimumFractionDigits: 2 }
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Guardar' : 'Agregar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
